import { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useTrips } from "./useTrips";
import type { TripWithId } from "./useTrips";
import type { SegmentWithId } from "./useSegments";
import type { Interaction } from "../types";

export interface EntityActivity extends Interaction {
  id: string;
}

export interface EntityHistoryData {
  /** Every activity logged at the entity on a trip the reader belongs to. */
  activities: EntityActivity[];
  /**
   * The activities those sit inside of (a ride's park check-in, and its
   * parents), keyed by id. Who was on a visit can be inherited from them.
   */
  ancestorsById: Record<string, EntityActivity>;
  /** The trips those activities came from, for names, zones and the party. */
  tripsById: Record<string, TripWithId>;
  /** Segments of those trips, for the day and zone an activity fell on. */
  segmentsByTrip: Record<string, SegmentWithId[]>;
  loading: boolean;
  /** Set when a batch failed; whatever did load is still returned. */
  error: boolean;
}

/**
 * How many trips one query may cover. The read rule on an activity does a
 * `get()` of its trip, and Firestore evaluates an `in` filter one value at a
 * time against a budget of 20 `get()`s per query — in the emulator the query
 * is refused somewhere between 15 and 19 trips. Ten keeps clear of that
 * whichever way the budget is counted.
 */
export const TRIP_BATCH_SIZE = 10;

/**
 * Everything logged at one entity across the reader's trips.
 *
 * The rules only let a query be read when its filters prove every row is
 * readable, and an activity is readable by its author or by members of its
 * trip. Filtering on the entity alone proves nothing; filtering on the entity
 * *and* a trip the reader belongs to proves it for every row. So the history
 * is found trip by trip, batched with `in`, over the same membership list the
 * trips page shows. That returns everything the party logged there; the
 * history view then keeps only the visits the reader was part of
 * (`utils/entityHistory.ts`, `activityIncludesUser`).
 *
 * Loaded once per entity rather than kept live: it's a look back, and the
 * page that changes it (logging a new visit) navigates away.
 */
export function useEntityHistory(entityId: string | undefined): EntityHistoryData {
  const { trips, loading: tripsLoading } = useTrips();
  const [activities, setActivities] = useState<EntityActivity[]>([]);
  const [segmentsByTrip, setSegmentsByTrip] = useState<Record<string, SegmentWithId[]>>({});
  const [ancestorsById, setAncestorsById] = useState<Record<string, EntityActivity>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Membership can change while the page is open (a trip created elsewhere
  // arriving), so the effect keys on the id list rather than the trips array,
  // which is rebuilt on every snapshot.
  const tripIdsKey = trips.map((t) => t.id).sort().join(",");

  useEffect(() => {
    if (!entityId || tripsLoading) return;
    const tripIds = tripIdsKey ? tripIdsKey.split(",") : [];
    if (tripIds.length === 0) {
      setActivities([]);
      setSegmentsByTrip({});
      setAncestorsById({});
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);

    (async () => {
      const found = new Map<string, EntityActivity>();
      let failed = false;
      const batches: string[][] = [];
      for (let i = 0; i < tripIds.length; i += TRIP_BATCH_SIZE) {
        batches.push(tripIds.slice(i, i + TRIP_BATCH_SIZE));
      }
      await Promise.all(
        batches.map(async (batch) => {
          try {
            const snap = await getDocs(
              query(
                collection(db, "interactions"),
                where("entityId", "==", entityId),
                where("tripId", "in", batch),
              ),
            );
            for (const d of snap.docs) {
              found.set(d.id, { id: d.id, ...(d.data() as Interaction) });
            }
          } catch {
            // A batch is refused as a whole when any of its trips can't be
            // resolved server-side yet (see resilientSnapshot). Report it and
            // keep whatever the other batches returned.
            failed = true;
          }
        }),
      );
      if (cancelled) return;

      // Only the trips that had something here need their segments; those
      // give the day an untimed activity fell on and the zone its times read in.
      const hitTripIds = [...new Set([...found.values()].map((a) => a.tripId))];
      const segments: Record<string, SegmentWithId[]> = {};
      const ancestors: Record<string, EntityActivity> = {};
      await Promise.all([
        ...hitTripIds.map(async (tripId) => {
          try {
            const snap = await getDocs(collection(db, "trips", tripId, "segments"));
            segments[tripId] = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as SegmentWithId);
          } catch {
            segments[tripId] = [];
          }
        }),
        // And the chain each visit sits inside of, since who was on a ride
        // may only be written on the park check-in above it. Chains are
        // short and mostly shared, so this is a handful of reads at most.
        ...[...found.values()].map(async (visit) => {
          let parentId = visit.parentInteractionId ?? null;
          const seen = new Set<string>([visit.id]);
          while (parentId && !seen.has(parentId)) {
            seen.add(parentId);
            const known = found.get(parentId) ?? ancestors[parentId];
            if (known) {
              parentId = known.parentInteractionId ?? null;
              continue;
            }
            try {
              const snap = await getDoc(doc(db, "interactions", parentId));
              if (!snap.exists()) break;
              const parent = { id: snap.id, ...(snap.data() as Interaction) };
              ancestors[parent.id] = parent;
              parentId = parent.parentInteractionId ?? null;
            } catch {
              break;
            }
          }
        }),
      ]);
      if (cancelled) return;

      setActivities([...found.values()]);
      setSegmentsByTrip(segments);
      setAncestorsById(ancestors);
      setError(failed);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [entityId, tripsLoading, tripIdsKey]);

  const tripsById = useMemo(() => {
    const map: Record<string, TripWithId> = {};
    for (const t of trips) map[t.id] = t;
    return map;
  }, [trips]);

  return {
    activities,
    ancestorsById,
    tripsById,
    segmentsByTrip,
    loading: loading || tripsLoading,
    error,
  };
}
